export const GAME_TITLE = "Realmfall: Shattered Crown";
export const SAVE_SCHEMA_VERSION = 3;
export const ASSET_SCHEMA_VERSION = 1;
export const FIXED_STEP = 1 / 60;
export const WORLD_SIZE = 64;

const ability = (id, name, type, cost, cooldown, damage, extra = {}) => ({
  id,
  name,
  type,
  cost,
  cooldown,
  damage,
  range: type === "melee" ? 2.6 : 11,
  ...extra,
});

export const CLASSES = Object.freeze({
  barbarian: {
    id: "barbarian",
    name: "Barbarian",
    resource: "Rage",
    color: "#ef795f",
    motto: "Fury becomes a shield.",
    base: {
      health: 155,
      resource: 100,
      armor: 26,
      power: 18,
      speed: 6.1,
      crit: 0.08,
    },
    resourceRule:
      "Basic attacks and damage taken build Rage; abilities spend it.",
    preferences: ["great-axe", "heavy-armor"],
    abilities: [
      ability("cleave", "Cleave", "melee", 0, 0.55, 18, {
        cone: 1.8,
        generates: 14,
      }),
      ability("ground-breaker", "Ground Breaker", "area", 25, 7, 34, {
        radius: 4,
        status: "stun",
      }),
      ability("war-cry", "War Cry", "buff", 20, 12, 0, { status: "fortified" }),
      ability("iron-rush", "Iron Rush", "dash", 18, 5, 24, {
        range: 7,
        status: "stun",
      }),
      ability("earthshatter", "Earthshatter", "ultimate", 100, 24, 95, {
        radius: 7,
        ultimate: true,
      }),
    ],
  },
  arcanist: {
    id: "arcanist",
    name: "Arcanist",
    resource: "Mana",
    color: "#71a9ff",
    motto: "The sky remembers every spark.",
    base: {
      health: 102,
      resource: 130,
      armor: 10,
      power: 23,
      speed: 6.3,
      crit: 0.1,
    },
    resourceRule: "Mana regenerates continuously; spells spend it.",
    preferences: ["staff", "focus"],
    abilities: [
      ability("arcane-bolt", "Arcane Bolt", "projectile", 4, 0.42, 20),
      ability("flame-orb", "Flame Orb", "projectile", 22, 5, 38, {
        radius: 2.8,
        status: "burn",
      }),
      ability("frost-ring", "Frost Ring", "area", 28, 8, 22, {
        radius: 5,
        status: "freeze",
      }),
      ability("blink", "Blink", "teleport", 18, 5, 0, { range: 8 }),
      ability("starfall", "Starfall", "ultimate", 100, 28, 112, {
        radius: 8,
        ultimate: true,
      }),
    ],
  },
  rogue: {
    id: "rogue",
    name: "Rogue",
    resource: "Energy",
    color: "#b59cf3",
    motto: "One shadow. No second warning.",
    base: {
      health: 112,
      resource: 100,
      armor: 14,
      power: 20,
      speed: 7.5,
      crit: 0.18,
    },
    resourceRule: "Energy returns quickly after a brief delay.",
    preferences: ["dagger", "light-armor"],
    abilities: [
      ability("twin-slash", "Twin Slash", "melee", 0, 0.38, 17, { hits: 2 }),
      ability("shadow-step", "Shadow Step", "teleport", 25, 6, 30, {
        range: 8,
      }),
      ability("venom-edge", "Venom Edge", "buff", 20, 9, 9, {
        status: "poison",
      }),
      ability("fan-of-blades", "Fan of Blades", "area", 35, 7, 38, {
        radius: 4.4,
      }),
      ability("nightfall", "Nightfall", "ultimate", 100, 25, 105, {
        radius: 7,
        ultimate: true,
        status: "vulnerable",
      }),
    ],
  },
  ranger: {
    id: "ranger",
    name: "Ranger",
    resource: "Focus",
    color: "#9bd769",
    motto: "Find the path no map could hold.",
    base: {
      health: 116,
      resource: 100,
      armor: 13,
      power: 20,
      speed: 6.8,
      crit: 0.14,
    },
    resourceRule: "Movement and accurate attacks build Focus.",
    preferences: ["bow", "leather-armor"],
    abilities: [
      ability("piercing-arrow", "Piercing Arrow", "projectile", 0, 0.5, 21, {
        pierce: 3,
        generates: 10,
      }),
      ability("multi-shot", "Multi-Shot", "cone", 24, 5, 15, {
        projectiles: 5,
      }),
      ability("snare-trap", "Snare Trap", "trap", 20, 8, 12, {
        radius: 3,
        status: "root",
      }),
      ability("evasive-roll", "Evasive Roll", "dash", 16, 4, 0, { range: 5 }),
      ability("storm-of-arrows", "Storm of Arrows", "ultimate", 100, 26, 108, {
        radius: 8,
        ultimate: true,
      }),
    ],
  },
  paladin: {
    id: "paladin",
    name: "Paladin",
    resource: "Holy Power",
    color: "#f1c96c",
    motto: "Carry dawn where it cannot reach.",
    base: {
      health: 142,
      resource: 100,
      armor: 30,
      power: 16,
      speed: 5.8,
      crit: 0.07,
    },
    resourceRule: "Smite and blocks build Holy Power; miracles spend it.",
    preferences: ["mace", "shield", "plate-armor"],
    abilities: [
      ability("smite", "Smite", "melee", 0, 0.58, 18, { generates: 12 }),
      ability("shield-bash", "Shield Bash", "melee", 18, 5, 24, {
        status: "stun",
        block: true,
      }),
      ability("consecrated-ground", "Consecrated Ground", "area", 28, 9, 22, {
        radius: 5,
        healing: 18,
      }),
      ability("healing-light", "Healing Light", "heal", 32, 9, 0, {
        healing: 45,
      }),
      ability("dawn-judgment", "Dawn Judgment", "ultimate", 100, 28, 92, {
        radius: 8,
        ultimate: true,
        healing: 35,
      }),
    ],
  },
  necromancer: {
    id: "necromancer",
    name: "Necromancer",
    resource: "Essence",
    color: "#73d4c1",
    motto: "Even dust may keep an oath.",
    base: {
      health: 108,
      resource: 110,
      armor: 12,
      power: 22,
      speed: 6.1,
      crit: 0.1,
    },
    resourceRule:
      "Deaths and life drain gather Essence; summons and bone magic spend it.",
    preferences: ["wand", "talisman"],
    abilities: [
      ability("bone-spear", "Bone Spear", "projectile", 0, 0.52, 21, {
        pierce: 2,
        generates: 8,
      }),
      ability("life-drain", "Life Drain", "channel", 18, 5, 28, {
        healing: 18,
      }),
      ability("raise-fallen", "Raise Fallen", "summon", 34, 9, 14, {
        summon: "dustbound",
        limit: 3,
      }),
      ability("corpse-burst", "Corpse Burst", "area", 24, 6, 42, {
        radius: 4.5,
        corpse: true,
      }),
      ability("legion-of-dust", "Legion of Dust", "ultimate", 100, 30, 96, {
        radius: 8,
        ultimate: true,
        summon: "legion",
      }),
    ],
  },
});

export const STATUS_DEFINITIONS = Object.freeze({
  burn: {
    name: "Burn",
    color: "#ff744f",
    duration: 5,
    interval: 1,
    maxStacks: 3,
    policy: "stack",
    resistance: "fire",
    icon: "▲",
  },
  poison: {
    name: "Poison",
    color: "#8fdd64",
    duration: 7,
    interval: 1,
    maxStacks: 4,
    policy: "stack",
    resistance: "poison",
    icon: "◆",
  },
  bleed: {
    name: "Bleed",
    color: "#f05d67",
    duration: 6,
    interval: 1,
    maxStacks: 3,
    policy: "stack",
    resistance: "physical",
    icon: "▼",
  },
  freeze: {
    name: "Freeze",
    color: "#89dcff",
    duration: 2.2,
    interval: 0,
    maxStacks: 1,
    policy: "refresh",
    resistance: "frost",
    icon: "✣",
  },
  stun: {
    name: "Stun",
    color: "#ffe680",
    duration: 1.5,
    interval: 0,
    maxStacks: 1,
    policy: "replace",
    resistance: "control",
    icon: "✦",
  },
  slow: {
    name: "Slow",
    color: "#98d9ee",
    duration: 4,
    interval: 0,
    maxStacks: 1,
    policy: "refresh",
    resistance: "frost",
    icon: "≋",
  },
  root: {
    name: "Root",
    color: "#7fc76b",
    duration: 3,
    interval: 0,
    maxStacks: 1,
    policy: "refresh",
    resistance: "control",
    icon: "♧",
  },
  weak: {
    name: "Weak",
    color: "#b8a2d6",
    duration: 5,
    interval: 0,
    maxStacks: 1,
    policy: "refresh",
    resistance: "arcane",
    icon: "▽",
  },
  vulnerable: {
    name: "Vulnerable",
    color: "#f39ba9",
    duration: 5,
    interval: 0,
    maxStacks: 1,
    policy: "refresh",
    resistance: "arcane",
    icon: "◇",
  },
  regeneration: {
    name: "Regeneration",
    color: "#7df0ad",
    duration: 6,
    interval: 1,
    maxStacks: 2,
    policy: "stack",
    resistance: null,
    icon: "+",
  },
  shield: {
    name: "Shield",
    color: "#6bc9f2",
    duration: 8,
    interval: 0,
    maxStacks: 1,
    policy: "replace",
    resistance: null,
    icon: "◈",
  },
  fortified: {
    name: "Fortified",
    color: "#e9c96f",
    duration: 6,
    interval: 0,
    maxStacks: 1,
    policy: "refresh",
    resistance: null,
    icon: "⬟",
  },
});

const enemy = (id, name, faction, role, health, damage, color, extra = {}) => ({
  id,
  name,
  faction,
  role,
  health,
  damage,
  color,
  speed: 2.7,
  range: role === "ranged" || role === "artillery" ? 9 : 2.2,
  ...extra,
});
export const ENEMIES = Object.freeze([
  enemy(
    "hollow-soldier",
    "Hollow Soldier",
    "Hollow Court",
    "melee",
    48,
    9,
    "#7e8f9e",
  ),
  enemy(
    "hollow-archer",
    "Hollow Archer",
    "Hollow Court",
    "ranged",
    38,
    8,
    "#718697",
  ),
  enemy(
    "hollow-knight",
    "Hollow Knight",
    "Hollow Court",
    "tank",
    86,
    12,
    "#637888",
    { armor: 25 },
  ),
  enemy(
    "hollow-priest",
    "Hollow Priest",
    "Hollow Court",
    "healer",
    44,
    6,
    "#b0a1c1",
  ),
  enemy(
    "hollow-captain",
    "Hollow Captain",
    "Hollow Court",
    "support",
    74,
    13,
    "#8a718f",
  ),
  enemy(
    "cult-initiate",
    "Cult Initiate",
    "Ashen Cult",
    "melee",
    42,
    10,
    "#c15e43",
  ),
  enemy(
    "flame-acolyte",
    "Flame Acolyte",
    "Ashen Cult",
    "ranged",
    39,
    10,
    "#e08344",
    { status: "burn" },
  ),
  enemy("ash-brute", "Ash Brute", "Ashen Cult", "charger", 92, 15, "#9b493a"),
  enemy(
    "cinder-summoner",
    "Cinder Summoner",
    "Ashen Cult",
    "summoner",
    51,
    8,
    "#d55f3b",
  ),
  enemy(
    "ember-seer",
    "Ember Seer",
    "Ashen Cult",
    "artillery",
    47,
    13,
    "#ff9b50",
  ),
  enemy("dire-wolf", "Dire Wolf", "Wildborn", "assassin", 38, 9, "#799064", {
    speed: 4.6,
  }),
  enemy("thorn-beast", "Thorn Beast", "Wildborn", "tank", 82, 12, "#6f8b4d"),
  enemy(
    "marsh-stalker",
    "Marsh Stalker",
    "Wildborn",
    "assassin",
    49,
    11,
    "#448774",
    { status: "poison" },
  ),
  enemy(
    "cave-spider",
    "Cave Spider",
    "Wildborn",
    "controller",
    35,
    7,
    "#796879",
    { status: "root" },
  ),
  enemy(
    "ancient-boar",
    "Ancient Boar",
    "Wildborn",
    "charger",
    88,
    14,
    "#897054",
  ),
  enemy(
    "skeleton-guard",
    "Skeleton Guard",
    "Restless Dead",
    "melee",
    46,
    9,
    "#cabf9e",
  ),
  enemy(
    "skeleton-archer",
    "Skeleton Archer",
    "Restless Dead",
    "ranged",
    36,
    9,
    "#b9ae90",
  ),
  enemy("wraith", "Wraith", "Restless Dead", "assassin", 43, 12, "#85b6b0", {
    speed: 4.1,
  }),
  enemy(
    "bone-mage",
    "Bone Mage",
    "Restless Dead",
    "controller",
    45,
    11,
    "#a3c8bd",
    { status: "slow" },
  ),
  enemy(
    "grave-hulk",
    "Grave Hulk",
    "Restless Dead",
    "tank",
    102,
    15,
    "#8c927e",
  ),
  enemy(
    "crystal-crawler",
    "Crystal Crawler",
    "Crystal Corruption",
    "melee",
    44,
    10,
    "#a875d2",
  ),
  enemy(
    "shard-sentinel",
    "Shard Sentinel",
    "Crystal Corruption",
    "tank",
    94,
    14,
    "#8b6ec5",
    { armor: 20 },
  ),
  enemy(
    "prism-wisp",
    "Prism Wisp",
    "Crystal Corruption",
    "ranged",
    34,
    12,
    "#d598e8",
    { speed: 4 },
  ),
  enemy(
    "fractured-golem",
    "Fractured Golem",
    "Crystal Corruption",
    "artillery",
    110,
    17,
    "#7568af",
  ),
]);

export const ELITE_AFFIXES = Object.freeze([
  { id: "bulwark", name: "Bulwark", health: 1.6, color: "#e8c875" },
  { id: "swift", name: "Swift", speed: 1.5, color: "#87e4d2" },
  { id: "vampiric", name: "Vampiric", leech: 0.25, color: "#c65b77" },
  { id: "molten", name: "Molten", status: "burn", color: "#f27742" },
  { id: "frostbound", name: "Frostbound", status: "slow", color: "#8fd9f0" },
  { id: "venomous", name: "Venomous", status: "poison", color: "#8bd35a" },
  { id: "mirror", name: "Mirror", reflect: 0.12, color: "#c89be8" },
  { id: "stormcall", name: "Stormcall", chain: true, color: "#89aaff" },
]);

export const MINIBOSSES = Object.freeze([
  { id: "thornjaw", name: "Thornjaw, Root-Tyrant", base: "thorn-beast" },
  { id: "malken", name: "Malken the Cinder-Sworn", base: "ember-seer" },
  { id: "fen-mother", name: "The Fen-Mother", base: "marsh-stalker" },
  { id: "rimeward", name: "Rimeward Ossuary", base: "grave-hulk" },
  { id: "prism-heart", name: "The Prism Heart", base: "fractured-golem" },
  { id: "veil-champion", name: "Veil Champion Arcos", base: "hollow-knight" },
]);

export const BOSSES = Object.freeze([
  {
    id: "briar-matron",
    name: "The Briar Matron",
    zone: "verdant-march",
    color: "#85b85c",
    loot: "Matron's Thorn",
    phases: ["Rootwake", "Briar Tempest", "Heartwood Unbound"],
  },
  {
    id: "varkos",
    name: "Varkos, Ashbound General",
    zone: "ashen-reach",
    color: "#e36a42",
    loot: "Varkos's Emberbrand",
    phases: ["Iron Oath", "Ashen Standard", "Last Conflagration"],
  },
  {
    id: "glass-devourer",
    name: "The Glass Devourer",
    zone: "moonfen",
    color: "#bd83d8",
    loot: "Devouring Prism",
    phases: ["Refraction", "Shattered Reflection", "Hungry Spectrum"],
  },
  {
    id: "saint-orun",
    name: "Saint Orun the Hollow",
    zone: "frostglass-highlands",
    color: "#92d7e8",
    loot: "Orun's Empty Halo",
    phases: ["Frozen Litany", "Silent Procession", "The Empty Saint"],
  },
  {
    id: "hollow-regent",
    name: "The Hollow Regent",
    zone: "hollow-crown-citadel",
    color: "#c9789b",
    loot: "Crownshard of Choice",
    phases: ["Regent Veiled", "Fivefold Crown", "Beyond the Hollow"],
  },
]);

export const REGIONS = Object.freeze([
  {
    id: "veilwatch",
    name: "Veilwatch Sanctuary",
    chapter: 0,
    color: "#70d0ad",
    terrain: "sanctuary",
    factions: [],
    position: [48, 56],
    description:
      "Permanent Warden refuge, crafting hall, vendors, codices, and portal chamber.",
  },
  {
    id: "verdant-march",
    name: "Verdant March",
    chapter: 1,
    color: "#79a95e",
    terrain: "grass",
    factions: ["Wildborn", "Hollow Court"],
    position: [29, 55],
    description:
      "Rain-bright ruins tangled beneath the Briar Matron's awakening roots.",
  },
  {
    id: "ashen-reach",
    name: "Ashen Reach",
    chapter: 2,
    color: "#d96842",
    terrain: "ash",
    factions: ["Ashen Cult"],
    position: [18, 31],
    description:
      "A red escarpment where oath-fires still consume the names of the dead.",
  },
  {
    id: "moonfen",
    name: "Moonfen",
    chapter: 3,
    color: "#737cbb",
    terrain: "marsh",
    factions: ["Wildborn", "Crystal Corruption"],
    position: [45, 27],
    description:
      "A mirrored wetland that remembers paths differently after dusk.",
  },
  {
    id: "frostglass-highlands",
    name: "Frostglass Highlands",
    chapter: 4,
    color: "#8bcfe0",
    terrain: "ice",
    factions: ["Restless Dead", "Crystal Corruption"],
    position: [69, 32],
    description:
      "Wind-carved cloisters and blue ice above the abandoned saint-road.",
  },
  {
    id: "hollow-crown-citadel",
    name: "Hollow Crown Citadel",
    chapter: 5,
    color: "#a76885",
    terrain: "hollow",
    factions: ["Hollow Court", "Crystal Corruption"],
    position: [72, 11],
    description:
      "The broken palace hangs over the wound where the Regent waits.",
  },
]);

export const QUESTS = Object.freeze([
  {
    id: "tutorial-awakening",
    type: "tutorial",
    title: "A Warden Wakes",
    chapter: 0,
    objectives: [
      { type: "move", target: 1, text: "Move through the training circle" },
      { type: "attack", target: 1, text: "Use a basic attack" },
      { type: "dodge", target: 1, text: "Dodge a telegraphed strike" },
      { type: "loot", target: 1, text: "Collect and equip the field blade" },
      { type: "checkpoint", target: 1, text: "Attune to the waystone" },
    ],
  },
  {
    id: "chapter-1",
    type: "campaign",
    title: "The Broken March",
    chapter: 1,
    objectives: [
      { type: "defeat", target: 8, text: "Break the Hollow patrol" },
      { type: "activate", target: 3, text: "Awaken verdant shrines" },
      {
        type: "boss",
        id: "briar-matron",
        target: 1,
        text: "Defeat the Briar Matron",
      },
    ],
  },
  {
    id: "chapter-2",
    type: "campaign",
    title: "Ashes of the Oath",
    chapter: 2,
    objectives: [
      { type: "collect", target: 4, text: "Recover ember sigils" },
      { type: "survive", target: 1, text: "Survive the oath-fire" },
      { type: "boss", id: "varkos", target: 1, text: "Defeat Varkos" },
    ],
  },
  {
    id: "chapter-3",
    type: "campaign",
    title: "Mirrors Beneath the Fen",
    chapter: 3,
    objectives: [
      { type: "explore", target: 3, text: "Chart reflected crossings" },
      { type: "puzzle", target: 1, text: "Align the fen mirrors" },
      {
        type: "boss",
        id: "glass-devourer",
        target: 1,
        text: "Defeat the Glass Devourer",
      },
    ],
  },
  {
    id: "chapter-4",
    type: "campaign",
    title: "The Empty Saint",
    chapter: 4,
    objectives: [
      { type: "escort", target: 1, text: "Guide Elys to the silent cloister" },
      { type: "activate", target: 4, text: "Ring the frozen bells" },
      { type: "boss", id: "saint-orun", target: 1, text: "Release Saint Orun" },
    ],
  },
  {
    id: "chapter-5",
    type: "campaign",
    title: "Crown of the Hollow",
    chapter: 5,
    objectives: [
      { type: "fragment", target: 4, text: "Set the recovered fragments" },
      {
        type: "boss",
        id: "hollow-regent",
        target: 1,
        text: "Defeat the Hollow Regent",
      },
      { type: "choice", target: 1, text: "Choose Asterra's crown" },
    ],
  },
  {
    id: "bounty-court",
    type: "bounty",
    title: "Empty Armor",
    chapter: 1,
    objectives: [
      {
        type: "defeat-faction",
        faction: "Hollow Court",
        target: 12,
        text: "Defeat Hollow Court soldiers",
      },
    ],
  },
  {
    id: "side-scout",
    type: "side",
    title: "The Scout's Last Marker",
    chapter: 1,
    objectives: [
      { type: "interact", target: 3, text: "Find Serah's trail markers" },
    ],
  },
  {
    id: "explore-fen",
    type: "exploration",
    title: "Stars Below",
    chapter: 3,
    objectives: [
      { type: "secret", target: 2, text: "Discover moonkennel secrets" },
    ],
  },
]);

export const COMPANIONS = Object.freeze([
  {
    id: "serah",
    name: "Serah, Veil Scout",
    role: "ranged",
    ability: "Gale Pin",
    color: "#80c88c",
    unlockChapter: 1,
  },
  {
    id: "doran",
    name: "Doran, Exiled Shieldbearer",
    role: "tank",
    ability: "Oathwall",
    color: "#d5a96c",
    unlockChapter: 2,
  },
  {
    id: "elys",
    name: "Elys, Runebound Scholar",
    role: "support",
    ability: "Runic Mend",
    color: "#8bbbe5",
    unlockChapter: 3,
  },
]);

export const RARITIES = Object.freeze({
  common: { name: "Common", color: "#d7dfdc", affixes: 0, weight: 54 },
  uncommon: { name: "Uncommon", color: "#78d28a", affixes: 1, weight: 25 },
  rare: { name: "Rare", color: "#6ba9ee", affixes: 2, weight: 13 },
  epic: { name: "Epic", color: "#b381e8", affixes: 3, weight: 6 },
  legendary: { name: "Legendary", color: "#e69b4c", affixes: 4, weight: 1.8 },
  mythic: { name: "Mythic", color: "#f16e99", affixes: 5, weight: 0.2 },
});

export const ITEM_BASES = Object.freeze([
  {
    id: "warden-blade",
    name: "Warden Blade",
    slot: "weapon",
    type: "sword",
    power: 8,
  },
  {
    id: "rift-axe",
    name: "Rift Axe",
    slot: "weapon",
    type: "great-axe",
    power: 12,
  },
  {
    id: "aster-staff",
    name: "Aster Staff",
    slot: "weapon",
    type: "staff",
    power: 11,
  },
  {
    id: "shade-knives",
    name: "Shade Knives",
    slot: "weapon",
    type: "dagger",
    power: 10,
  },
  {
    id: "march-bow",
    name: "March Bow",
    slot: "weapon",
    type: "bow",
    power: 10,
  },
  { id: "sun-mace", name: "Sun Mace", slot: "weapon", type: "mace", power: 10 },
  {
    id: "bone-wand",
    name: "Bone Wand",
    slot: "weapon",
    type: "wand",
    power: 10,
  },
  {
    id: "veil-shield",
    name: "Veil Shield",
    slot: "offHand",
    type: "shield",
    armor: 12,
  },
  {
    id: "runic-focus",
    name: "Runic Focus",
    slot: "offHand",
    type: "focus",
    power: 7,
  },
  {
    id: "warden-helm",
    name: "Warden Helm",
    slot: "helmet",
    type: "heavy-armor",
    armor: 7,
  },
  {
    id: "march-coat",
    name: "March Coat",
    slot: "armor",
    type: "leather-armor",
    armor: 10,
  },
  {
    id: "oath-plate",
    name: "Oath Plate",
    slot: "armor",
    type: "plate-armor",
    armor: 16,
  },
  {
    id: "gripwraps",
    name: "Gripwraps",
    slot: "gloves",
    type: "armor",
    armor: 4,
  },
  {
    id: "pathfinder-boots",
    name: "Pathfinder Boots",
    slot: "boots",
    type: "armor",
    armor: 4,
  },
  {
    id: "shard-pendant",
    name: "Shard Pendant",
    slot: "necklace",
    type: "jewelry",
    power: 5,
  },
  {
    id: "echo-ring",
    name: "Echo Ring",
    slot: "ring",
    type: "jewelry",
    power: 3,
  },
]);

export const AFFIXES = Object.freeze([
  {
    id: "strength",
    name: "Strength",
    stat: "power",
    min: 2,
    max: 10,
    types: [
      "sword",
      "great-axe",
      "mace",
      "armor",
      "heavy-armor",
      "plate-armor",
    ],
  },
  {
    id: "dexterity",
    name: "Dexterity",
    stat: "crit",
    min: 0.01,
    max: 0.06,
    types: ["dagger", "bow", "leather-armor", "jewelry"],
  },
  {
    id: "intellect",
    name: "Intellect",
    stat: "power",
    min: 2,
    max: 10,
    types: ["staff", "wand", "focus", "jewelry"],
  },
  {
    id: "vitality",
    name: "Vitality",
    stat: "health",
    min: 8,
    max: 35,
    types: [
      "armor",
      "heavy-armor",
      "light-armor",
      "leather-armor",
      "plate-armor",
      "jewelry",
    ],
  },
  {
    id: "warding",
    name: "Armor",
    stat: "armor",
    min: 3,
    max: 14,
    types: [
      "armor",
      "heavy-armor",
      "light-armor",
      "leather-armor",
      "plate-armor",
      "shield",
    ],
  },
  {
    id: "emberskin",
    name: "Fire Resistance",
    stat: "fireResistance",
    min: 0.04,
    max: 0.18,
    types: ["armor", "jewelry"],
  },
  {
    id: "rimeguard",
    name: "Frost Resistance",
    stat: "frostResistance",
    min: 0.04,
    max: 0.18,
    types: ["armor", "jewelry"],
  },
  {
    id: "venomward",
    name: "Poison Resistance",
    stat: "poisonResistance",
    min: 0.04,
    max: 0.18,
    types: ["armor", "jewelry"],
  },
  {
    id: "precision",
    name: "Critical Chance",
    stat: "crit",
    min: 0.01,
    max: 0.05,
    types: [
      "sword",
      "great-axe",
      "mace",
      "dagger",
      "bow",
      "staff",
      "wand",
      "jewelry",
    ],
  },
  {
    id: "haste",
    name: "Attack Speed",
    stat: "attackSpeed",
    min: 0.03,
    max: 0.12,
    types: [
      "sword",
      "great-axe",
      "mace",
      "dagger",
      "bow",
      "staff",
      "wand",
      "jewelry",
    ],
  },
  {
    id: "windstep",
    name: "Movement Speed",
    stat: "speed",
    min: 0.12,
    max: 0.45,
    types: ["armor", "jewelry"],
  },
  {
    id: "flow",
    name: "Resource Generation",
    stat: "resourceGeneration",
    min: 0.04,
    max: 0.18,
    types: ["jewelry", "focus", "shield"],
  },
]);

export const SKILL_TREES = Object.freeze(
  Object.fromEntries(
    Object.values(CLASSES).map((klass) => [
      klass.id,
      [
        {
          id: `${klass.id}-vigor`,
          name: "Warden's Vigor",
          description: "+8 maximum health per rank",
          max: 3,
          requires: null,
        },
        {
          id: `${klass.id}-edge`,
          name: `${klass.abilities[0].name} Mastery`,
          description: "+8% basic ability damage per rank",
          max: 3,
          requires: `${klass.id}-vigor`,
        },
        {
          id: `${klass.id}-flow`,
          name: `${klass.resource} Flow`,
          description: "+7% resource generation per rank",
          max: 3,
          requires: `${klass.id}-vigor`,
        },
        {
          id: `${klass.id}-ward`,
          name: "Veilward",
          description: "+5 armor per rank",
          max: 2,
          requires: `${klass.id}-edge`,
        },
        {
          id: `${klass.id}-apex`,
          name: `${klass.abilities[4].name}: Ascendant`,
          description: "Ultimate deals 25% more damage",
          max: 1,
          requires: `${klass.id}-flow`,
        },
      ],
    ]),
  ),
);

export const ACHIEVEMENTS = Object.freeze(
  [
    ["first-warden", "A Name in the Ledger", "Create a character"],
    ["tutorial", "Veil Initiate", "Complete the Tutorial"],
    ["first-elite", "A Cut Above", "Defeat an elite"],
    ["first-boss", "Crown-Breaker", "Defeat a major boss"],
    ["first-fragment", "Shardbearer", "Recover a Crown fragment"],
    ["campaign", "Realmfall", "Complete the campaign"],
    ["alternate-ending", "A Crown Refused", "Unlock an alternate ending"],
    ["level-10", "Seasoned Warden", "Reach level 10"],
    ["legendary", "Orange Dawn", "Obtain a Legendary item"],
    ["mythic", "Beyond Rarity", "Obtain a Mythic item"],
    ["challenge-first", "Into the Crucible", "Complete a Challenge Dungeon"],
    ["challenge-ten", "Tenfold Veil", "Complete tier 10"],
    ["companions", "Never Alone", "Recruit every companion"],
    ["regions", "Asterra Cartographer", "Discover every region"],
    ["secrets", "Behind the Masonry", "Find five secret rooms"],
    ["boss-flawless", "Untouched Oath", "Defeat a boss without dying"],
    ["boss-no-potion", "Clear-Eyed", "Defeat a boss without potions"],
    ["all-ultimates", "Sixfold Power", "Use every class ultimate"],
    [
      "many-campaigns",
      "Many Faces of the Veil",
      "Complete the campaign with three classes",
    ],
  ].map(([id, name, description]) => ({ id, name, description })),
);

export const VENDORS = Object.freeze([
  {
    id: "maelin",
    name: "Maelin's Provisions",
    role: "General Merchant",
    currency: "gold",
  },
  {
    id: "brannoc",
    name: "Brannoc Ironhand",
    role: "Blacksmith",
    currency: "gold",
  },
  {
    id: "sova",
    name: "Sova of the Stillroom",
    role: "Alchemist",
    currency: "gold",
  },
  {
    id: "nariel",
    name: "Nariel Runeweaver",
    role: "Enchanter",
    currency: "veilShards",
  },
  {
    id: "ors",
    name: "Ors the Unbroken",
    role: "Challenge Vendor",
    currency: "challengeMarks",
  },
]);

export const CHALLENGE_MODIFIERS = Object.freeze([
  {
    id: "empowered",
    name: "Empowered Elites",
    effect: "Elite health and damage increased",
  },
  { id: "scarcity", name: "Scarce Mercy", effect: "Healing reduced by 40%" },
  {
    id: "cinder-floor",
    name: "Cinder Floor",
    effect: "Burning ground erupts in combat",
  },
  {
    id: "quicksilver",
    name: "Quicksilver",
    effect: "Enemy projectiles travel 35% faster",
  },
  {
    id: "fracture",
    name: "Fractured Arena",
    effect: "Additional crystal hazards",
  },
  {
    id: "crowned",
    name: "Crowned Foes",
    effect: "Bosses gain an additional pattern",
  },
]);

export const STORAGE_KEYS = Object.freeze({
  settings: "realmfallSettings",
  profile: "realmfallProfile",
  fallback: "realmfallSaveSlots",
});
